import React, { useState, useEffect, useCallback } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import api from '../lib/api';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileEdit,
  Loader2,
  MessageSquare,
  Paperclip,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('de-DE', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

const formatTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const WEEKDAYS_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const formatDate = (isoDate) => {
  if (!isoDate) return '-';
  const [y, m, d] = String(isoDate).split('-');
  if (!y || !m || !d) return isoDate;
  const wd = new Date(Number(y), Number(m) - 1, Number(d)).getDay();
  return `${WEEKDAYS_DE[wd]}, ${d}.${m}.${y}`;
};

const getAttachmentUrl = (filename) => {
  if (!filename) return null;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const token = (localStorage.getItem('token') || sessionStorage.getItem('token')) || '';
  return `${base}/api/time-tracking/corrections/attachments/${filename}?token=${encodeURIComponent(token)}`;
};

export default function TimeCorrections({ embedded = false }) {
  const { isAdmin, hasPermission } = useAuth();
  // Reviewer kann Zeitkorrekturen anderer Mitarbeiter einsehen + genehmigen
  const isAdminView = isAdmin || hasPermission('time_tracking', 'execute');
  
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadCorrections = useCallback(async () => {
    try {
      const status = activeTab === 'all' ? undefined : activeTab;
      const response = await api.get('/time-tracking/corrections', {
        params: { status }
      });
      setCorrections(response.data);
    } catch (error) {
      console.error('Failed to load corrections:', error);
      toast.error('Fehler beim Laden der Korrekturanträge');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadCorrections();
  }, [loadCorrections]);

  const openReviewDialog = (correction) => {
    setSelectedCorrection(correction);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleReview = async (status) => {
    if (!selectedCorrection) return;
    
    setSubmitting(true);
    try {
      await api.put(`/time-tracking/corrections/${selectedCorrection.id}`, {
        status,
        review_notes: reviewNotes || null
      });
      toast.success(status === 'approved' ? 'Korrektur genehmigt' : 'Korrektur abgelehnt');
      setReviewDialogOpen(false);
      loadCorrections();
    } catch (error) {
      toast.error('Fehler beim Bearbeiten');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-500"><AlertCircle className="h-3 w-3 mr-1" />Ausstehend</Badge>;
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Genehmigt</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Abgelehnt</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[500px] rounded-xl" />
        </div>
      </MaybeLayout>
    );
  }

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6 animate-fade-in" data-testid="time-corrections-page">
        {/* Header */}
        <div>
          <h1 className="font-heading font-bold text-3xl text-foreground">Korrekturanträge</h1>
          <p className="text-muted-foreground mt-1">
            {isAdminView ? 'Zeitkorrekturen prüfen und genehmigen' : 'Ihre Korrekturanträge'}
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <AlertCircle className="h-4 w-4" />
              Ausstehend
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-2">
              <CheckCircle className="h-4 w-4" />
              Genehmigt
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-2">
              <XCircle className="h-4 w-4" />
              Abgelehnt
            </TabsTrigger>
            <TabsTrigger value="all">Alle</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="font-heading">
                  {activeTab === 'pending' ? 'Ausstehende Anträge' :
                   activeTab === 'approved' ? 'Genehmigte Anträge' :
                   activeTab === 'rejected' ? 'Abgelehnte Anträge' : 'Alle Anträge'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        {isAdminView && <TableHead>Mitarbeiter</TableHead>}
                        <TableHead>Original</TableHead>
                        <TableHead>Gewünscht</TableHead>
                        <TableHead>Grund</TableHead>
                        <TableHead>Status</TableHead>
                        {isAdminView && activeTab === 'pending' && <TableHead>Aktion</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {corrections.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isAdminView ? 7 : 5} className="text-center text-muted-foreground py-8">
                            <FileEdit className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            Keine Korrekturanträge
                          </TableCell>
                        </TableRow>
                      ) : (
                        corrections.map((correction) => {
                          const origDate = correction.original_data?.date;
                          const reqDate = correction.requested_data?.date || origDate;
                          const origBreak = correction.original_data?.total_break_minutes ?? 0;
                          const reqBreak = correction.requested_data?.total_break_minutes ?? origBreak;
                          const dateChanged = reqDate && reqDate !== origDate;
                          const breakChanged = parseInt(reqBreak, 10) !== parseInt(origBreak, 10);
                          return (
                          <TableRow key={correction.id}>
                            <TableCell className="font-medium">
                              {formatDate(origDate)}
                              {dateChanged && (
                                <span className="block text-xs text-primary">→ {formatDate(reqDate)}</span>
                              )}
                            </TableCell>
                            {isAdminView && (
                              <TableCell>{correction.employee_name}</TableCell>
                            )}
                            <TableCell className="font-mono text-sm">
                              {formatTime(correction.original_data.clock_in)} - {formatTime(correction.original_data.clock_out)}
                              <span className="block text-xs text-muted-foreground">
                                Pause: {origBreak} min
                              </span>
                            </TableCell>
                            <TableCell className="font-mono text-sm text-primary">
                              {formatTime(correction.requested_data.clock_in)} - {formatTime(correction.requested_data.clock_out)}
                              <span className={`block text-xs ${breakChanged ? 'text-primary' : 'text-muted-foreground'}`}>
                                Pause: {reqBreak} min
                              </span>
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              <div className="truncate" title={correction.reason}>{correction.reason}</div>
                              {correction.attachment?.filename && (
                                <a
                                  href={getAttachmentUrl(correction.attachment.filename)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                                  data-testid={`correction-attachment-link-${correction.id}`}
                                >
                                  <Paperclip className="h-3 w-3" />
                                  {correction.attachment.original_name || 'Anhang'}
                                </a>
                              )}
                            </TableCell>
                            <TableCell>{getStatusBadge(correction.status)}</TableCell>
                            {isAdminView && activeTab === 'pending' && (
                              <TableCell>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openReviewDialog(correction)}
                                >
                                  Prüfen
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Review Dialog */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Korrekturantrag prüfen</DialogTitle>
            </DialogHeader>
            
            {selectedCorrection && (
              <div className="space-y-4">
                <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Mitarbeiter:</span>
                    <span className="font-medium">{selectedCorrection.employee_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Datum:</span>
                    <span className="font-medium">
                      {formatDate(selectedCorrection.original_data.date)}
                      {selectedCorrection.requested_data?.date && selectedCorrection.requested_data.date !== selectedCorrection.original_data.date && (
                        <span className="text-primary"> → {formatDate(selectedCorrection.requested_data.date)}</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Original</Label>
                    <div className="p-3 bg-gray-100 rounded-lg font-mono text-sm">
                      {formatTime(selectedCorrection.original_data.clock_in)} - {formatTime(selectedCorrection.original_data.clock_out)}
                      <div className="text-xs text-muted-foreground font-sans mt-1">
                        Pause: {selectedCorrection.original_data.total_break_minutes ?? 0} min
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-primary">Gewünscht</Label>
                    <div className="p-3 bg-primary/10 rounded-lg font-mono text-sm text-primary">
                      {formatTime(selectedCorrection.requested_data.clock_in)} - {formatTime(selectedCorrection.requested_data.clock_out)}
                      <div className="text-xs font-sans mt-1">
                        Pause: {selectedCorrection.requested_data?.total_break_minutes ?? selectedCorrection.original_data?.total_break_minutes ?? 0} min
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Begründung</Label>
                  <div className="p-3 bg-secondary/50 rounded-lg text-sm">
                    {selectedCorrection.reason}
                  </div>
                </div>

                {selectedCorrection.attachment?.filename && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Paperclip className="h-4 w-4" /> Nachweis</Label>
                    <a
                      href={getAttachmentUrl(selectedCorrection.attachment.filename)}
                      target="_blank"
                      rel="noreferrer"
                      className="block p-3 bg-secondary/50 rounded-lg text-sm text-primary hover:underline truncate"
                      data-testid="review-attachment-link"
                    >
                      {selectedCorrection.attachment.original_name || selectedCorrection.attachment.filename}
                    </a>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="review-notes">Anmerkung (optional)</Label>
                  <Textarea
                    id="review-notes"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Anmerkung zur Entscheidung..."
                    rows={2}
                  />
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                variant="destructive"
                onClick={() => handleReview('rejected')}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 mr-1" />}
                Ablehnen
              </Button>
              <Button
                onClick={() => handleReview('approved')}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Genehmigen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MaybeLayout>
  );
}
